import {
  GetMediaDocument,
  type MediaType,
  SearchMediaDocument,
  TranscribeMediaDocument,
  type TranscribeMediaMutation,
  type TranscribeMediaMutationVariables,
  UpdateMediaMetadataDocument,
  type UpdateMediaMetadataMutation,
  type UpdateMediaMetadataMutationVariables,
} from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { formatDuration, getMediaConfig, hasAudioText, hasDuration } from "../config";
import { MediaLinksPanel, TagsSection } from "../forms";
import { MediaPreview } from "../previews";
import type { MediaItem } from "../types";
import { EditPreviewPanel } from "./EditPreviewPanel";
import { ModalShell } from "./ModalShell";

interface MediaEditModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Media item to edit */
  media: MediaItem;
}

/**
 * MediaEditModal - Generic edit modal for all media types
 *
 * Uses configuration system to adapt behavior per media type.
 * Handles metadata editing, tags, and media linking.
 */
export function MediaEditModal({ isOpen, onClose, media }: MediaEditModalProps) {
  const config = getMediaConfig(media.type);
  const showAudioText = hasAudioText(media.type);
  const showDuration = hasDuration(media.type);

  // State
  const [audioText, setAudioText] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [showReplace, setShowReplace] = useState(false);
  const [selectedLinks, setSelectedLinks] = useState<Partial<Record<MediaType, string[]>>>({});
  const [error, setError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);

  // Mutations
  const [updateMetadata, { loading }] = useMutation<
    UpdateMediaMetadataMutation,
    UpdateMediaMetadataMutationVariables
  >(UpdateMediaMetadataDocument, {
    // No refetchQueries - Apollo normalization should handle it
    // Same as MediaTableRow inline edit (which works!)
    onCompleted: () => {
      setError(null);
      toast.success(`${config.label} updated`);
      onClose();
    },
    onError: (err) => {
      setError(err.message);
      toast.error(err.message);
    },
  });

  const [transcribeMedia] = useMutation<TranscribeMediaMutation, TranscribeMediaMutationVariables>(
    TranscribeMediaDocument,
    {
      // No refetchQueries - transcription is read-only, doesn't save to DB
      // User saves via updateMetadata when they click "Save"
      onCompleted: (data) => {
        const result = data.transcribeMedia;

        if (result.success && result.text) {
          // Update local state only - not saved to DB yet
          setAudioText(result.text);
          setTranscriptionError(null);
          toast.success(`Transcription complete! (${result.duration?.toFixed(1)}s)`);
        } else {
          setTranscriptionError(result.error || "Transcription failed");
          toast.error(result.error || "Transcription failed");
        }

        setIsTranscribing(false);
      },
      onError: (err) => {
        setTranscriptionError(err.message);
        toast.error(`Transcription failed: ${err.message}`);
        setIsTranscribing(false);
      },
    }
  );

  // Reset state when modal opens/closes or media changes
  useEffect(() => {
    if (isOpen) {
      // Initialize state from media
      setAudioText("audioText" in media && media.audioText ? media.audioText : "");
      setTags(media.tags || []);
      setShowReplace(false);
      setError(null);
      setSelectedLinks({});
    }
  }, [isOpen, media]);

  // Get duration for audio/video
  const duration = showDuration && "duration" in media ? media.duration : undefined;

  // Handle link changes
  const handleLinksChange = (type: MediaType, ids: string[]) => {
    setSelectedLinks((prev) => ({
      ...prev,
      [type]: ids,
    }));
  };

  // Handle transcription
  const handleGenerateTranscription = async () => {
    setIsTranscribing(true);
    setTranscriptionError(null);

    await transcribeMedia({
      variables: {
        mediaId: media.id,
      },
    });
  };

  // Handle save
  const handleSave = async () => {
    if (tags.length === 0) {
      setError("Please add at least one tag");
      return;
    }

    setError(null);

    await updateMetadata({
      variables: {
        input: {
          id: media.id,
          ...(showAudioText && { audioText: audioText.trim() || undefined }),
          tags: tags.length > 0 ? tags : undefined,
        },
      },
    });
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={loading ? () => {} : onClose}
      title={config.editTitle}
      leftPanel={
        <div className="space-y-6">
          {/* File Info Section */}
          <section>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {config.label} file
            </label>

            {/* File Info */}
            <div className="mb-3 rounded-md border border-gray-200 p-3">
              <MediaPreview
                url={media.url}
                filename={media.filename}
                type={media.type}
                duration={duration}
                variant="inline"
              />
            </div>

            {/* Audio Text Input (for audio/video only) */}
            {showAudioText && (
              <>
                <label htmlFor="audioText" className="mb-1 block text-xs font-medium text-gray-700">
                  Description / Transcription
                </label>
                <textarea
                  id="audioText"
                  value={audioText}
                  onChange={(e) => setAudioText(e.target.value)}
                  placeholder="Enter description or transcription..."
                  rows={4}
                  disabled={loading}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </>
            )}

            {/* Replace File Button */}
            <button
              type="button"
              onClick={() => setShowReplace(!showReplace)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              disabled={loading}
            >
              {showReplace ? "Cancel replace" : `Replace ${config.label.toLowerCase()} file`}
            </button>
          </section>

          {/* Tags Section */}
          <TagsSection
            value={tags}
            onChange={setTags}
            required
            helpText={`Enter tags for this ${config.label.toLowerCase()}. Separate tags by commas.`}
          />

          {/* Media Links */}
          <MediaLinksPanel
            sourceMediaType={media.type}
            sourceMediaId={media.id}
            selectedLinks={selectedLinks}
            onLinksChange={handleLinksChange}
          />
        </div>
      }
      rightPanel={
        <EditPreviewPanel
          media={media}
          showReplace={showReplace}
          audioText={audioText}
          isTranscribing={isTranscribing}
          transcriptionError={transcriptionError}
          onGenerateTranscription={handleGenerateTranscription}
        />
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </>
      }
      error={error}
    />
  );
}

export default MediaEditModal;
